/**
 * The two settings that invoices depend on: what the business prints on a tax
 * invoice, and whether Xero is connected.
 *
 * Both are office-only on the server; the screen shows them read-only to
 * everyone else rather than hiding them, because a tech asked "what's our
 * ABN?" on site should be able to find it.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError, api, readToken } from "../lib/api";
import { money } from "../lib/format";
import type { OrgOut } from "../lib/types";
import { type ServiceText, serviceBody, serviceTextFrom } from "../lib/servicerates";
import { ServiceCallSettings } from "./ServiceCallSettings";
import { Card, Chip } from "./ui";

type Editable = Omit<
  OrgOut,
  | "timezone"
  | "tax_rate_bp"
  | "default_cpi_uplift_bp"
  | "has_logo"
  | "service_call_fee_cents"
  | "service_call_minutes_included"
  | "service_hourly_rate_cents"
  | "service_km_included"
  | "service_km_rate_cents"
>;
type FieldDef = { key: keyof Editable; label: string; hint?: string; wide?: boolean; long?: boolean };

/** Grouped the way the business thinks of them, and the way ServiceM8's
 *  company profile groups them — so moving across is filling in the same
 *  boxes in the same order. */
const GROUPS: { title: string; fields: FieldDef[] }[] = [
  {
    title: "The business",
    fields: [
      { key: "name", label: "Legal name" },
      { key: "trading_name", label: "Trading name", hint: "What customers see, if different." },
      { key: "abn", label: "ABN", hint: "Required on a tax invoice." },
      { key: "acn", label: "ACN" },
      { key: "licence_number", label: "Security licence no." },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Accounts email" },
      { key: "website", label: "Website" },
    ],
  },
  {
    title: "Address",
    fields: [
      { key: "address_line1", label: "Street", wide: true },
      { key: "address_line2", label: "Line 2", wide: true },
      { key: "suburb", label: "Suburb" },
      { key: "state", label: "State" },
      { key: "postcode", label: "Postcode" },
    ],
  },
  {
    title: "Getting paid",
    fields: [
      { key: "bank_name", label: "Bank" },
      { key: "bank_account_name", label: "Account name" },
      { key: "bank_bsb", label: "BSB" },
      { key: "bank_account_number", label: "Account number" },
      { key: "payment_options", label: "Payment options", hint: "e.g. EFT, credit card (1.5% surcharge)", wide: true },
      {
        key: "payment_terms",
        label: "Payment terms",
        hint: "Leave empty to print each invoice's own due date.",
        wide: true,
      },
      {
        key: "sales_account_code",
        label: "Xero sales account",
        hint: "The account invoice lines post to. 200 is Sales in Xero's Australian chart.",
      },
    ],
  },
  {
    title: "Words on documents",
    fields: [
      { key: "quote_terms", label: "Quote terms and conditions", wide: true, long: true },
      { key: "invoice_note", label: "Closing note on invoices", wide: true, long: true },
    ],
  },
];

export function BusinessDetails({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const org = useQuery({ queryKey: ["org"], queryFn: () => api.org.get() });
  const [draft, setDraft] = useState<Partial<Editable>>({});
  // The service-call boxes are typed dollars; only the ones touched are kept.
  const [svcDraft, setSvcDraft] = useState<Partial<ServiceText>>({});
  const [said, setSaid] = useState<{ tone: "info" | "crit"; text: string } | null>(null);
  const [logoKey, setLogoKey] = useState(0);

  const save = useMutation({
    mutationFn: () => api.org.update({ ...draft, ...(svcBody ?? {}) }),
    onSuccess: (next) => {
      queryClient.setQueryData(["org"], next);
      setDraft({});
      setSvcDraft({});
      setSaid({ tone: "info", text: "Saved. Every document from now on carries it." });
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't save." }),
  });
  const logo = useMutation({
    mutationFn: (file: File | null) => (file ? api.org.uploadLogo(file) : api.org.removeLogo()),
    onSuccess: (next) => {
      queryClient.setQueryData(["org"], next);
      setLogoKey((k) => k + 1);
      setSaid({ tone: "info", text: next.has_logo ? "Logo saved." : "Logo removed." });
    },
    onError: (err) => setSaid({ tone: "crit", text: err instanceof ApiError ? err.message : "Couldn't save the logo." }),
  });

  const savedSvc = serviceTextFrom(org.data ?? {});
  const svcText: ServiceText = { ...savedSvc, ...svcDraft };
  const svcDirty = Object.keys(svcDraft).length > 0;
  // null while any box isn't valid, which also holds Save back.
  const svcBody = svcDirty ? serviceBody(svcText) : undefined;

  if (!org.data) return <Card title="Business details"><p className="tiny muted">Loading…</p></Card>;
  const data = org.data;
  const value = (k: keyof Editable) => (k in draft ? (draft[k] ?? "") : (data[k] ?? ""));
  const dirty = Object.keys(draft).length > 0 || svcDirty;
  const noBank = !data.bank_bsb || !data.bank_account_number;

  return (
    <Card title="Business details" subtitle="What goes on every quote, invoice and docket">
      {(!data.abn || noBank) && (
        <p className="notice notice-warn">
          {!data.abn ? "No ABN yet — an invoice without one isn't a valid tax invoice. " : ""}
          {noBank ? "No bank details yet — customers won't know how to pay." : ""}
        </p>
      )}

      <h3 className="sub-head">Logo</h3>
      <div className="logo-row">
        {data.has_logo ? (
          <LogoPreview key={logoKey} />
        ) : (
          <span className="tiny muted">No logo — the built-in documents use the business name.</span>
        )}
        {canEdit && (
          <>
            <label className="btn">
              {data.has_logo ? "Replace" : "Upload"}
              <input
                type="file"
                accept="image/png,image/jpeg"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) logo.mutate(file);
                  e.target.value = "";
                }}
              />
            </label>
            {data.has_logo && (
              <button className="btn" type="button" onClick={() => logo.mutate(null)}>
                Remove
              </button>
            )}
          </>
        )}
      </div>

      {GROUPS.map((group) => (
        <div key={group.title}>
          <h3 className="sub-head">{group.title}</h3>
          <div className="form-grid">
            {group.fields.map((f) => (
              <label key={f.key} className="field" style={f.wide ? { gridColumn: "1 / -1" } : undefined}>
                <span>{f.label}</span>
                {f.long ? (
                  <textarea
                    rows={4}
                    value={String(value(f.key))}
                    disabled={!canEdit}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                ) : (
                  <input
                    value={String(value(f.key))}
                    disabled={!canEdit}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  />
                )}
                {f.hint && <small className="tiny muted">{f.hint}</small>}
              </label>
            ))}
          </div>
        </div>
      ))}

      <ServiceCallSettings
        text={svcText}
        saved={savedSvc}
        canEdit={canEdit}
        onChange={(key, value) => setSvcDraft((d) => ({ ...d, [key]: value }))}
      />

      {said && (
        <p className={`notice notice-${said.tone}`} role={said.tone === "crit" ? "alert" : "status"} style={{ marginTop: 10 }}>
          {said.text}
        </p>
      )}
      {canEdit && (
        <div className="btn-row sticky-actions" style={{ marginTop: 12 }}>
          <button className="btn btn-primary" type="button" disabled={!dirty || svcBody === null || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
          {dirty && (
            <button
              className="btn"
              type="button"
              onClick={() => {
                setDraft({});
                setSvcDraft({});
              }}
            >
              Undo changes
            </button>
          )}
        </div>
      )}
    </Card>
  );
}

/** The logo is behind the sign-in token, so it is fetched, not linked. */
function LogoPreview() {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    const token = readToken();
    void fetch("/v1/org/logo", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (blob) {
          url = URL.createObjectURL(blob);
          setSrc(url);
        }
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, []);
  return src ? <img src={src} alt="Business logo" className="logo-preview" /> : <span className="tiny muted">…</span>;
}

const RETURN_COPY: Record<string, { tone: "info" | "crit"; text: string }> = {
  connected: { tone: "info", text: "Xero is connected." },
  cancelled: { tone: "crit", text: "Xero wasn't connected — the login was cancelled." },
  refused: { tone: "crit", text: "That connection link wasn't started here, or has expired. Press Connect again." },
  failed: { tone: "crit", text: "Xero didn't finish connecting." },
};

export function XeroSettings({ canEdit }: { canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [problem, setProblem] = useState<string | null>(null);

  const status = useQuery({ queryKey: ["accounting-status"], queryFn: () => api.accounting.status() });
  const attention = useQuery({
    queryKey: ["accounting-exports", "attention"],
    queryFn: () => api.accounting.exports(true),
    enabled: Boolean(status.data?.connected),
  });

  // Coming back from Xero's login page: say what happened, once.
  const returned = params.get("xero");
  const returnedDetail = params.get("detail");
  useEffect(() => {
    if (returned) void queryClient.invalidateQueries({ queryKey: ["accounting-status"] });
  }, [returned, queryClient]);

  const connect = useMutation({
    mutationFn: () => api.accounting.connect(),
    onSuccess: ({ authorize_url }) => {
      window.location.href = authorize_url;
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't start the connection."),
  });
  const disconnect = useMutation({
    mutationFn: () => api.accounting.disconnect(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["accounting-status"] }),
  });
  const push = useMutation({
    mutationFn: () => api.accounting.push(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["accounting-status"] });
      void queryClient.invalidateQueries({ queryKey: ["accounting-exports"] });
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't push."),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.accounting.retry(id),
    onSuccess: () => push.mutate(),
  });

  const s = status.data;
  const notice = returned ? RETURN_COPY[returned] : null;

  return (
    <Card title="Xero" subtitle="Invoices go to Xero once the customer has them">
      <div id="xero" />
      {notice && (
        <p className={`notice notice-${notice.tone}`} role="status">
          {notice.text}
          {returnedDetail ? ` ${returnedDetail}` : ""}{" "}
          <button className="linkish" type="button" onClick={() => setParams({}, { replace: true })}>
            Dismiss
          </button>
        </p>
      )}
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      {!s ? (
        <p className="tiny muted">Checking…</p>
      ) : !s.configured ? (
        <>
          <p>
            <Chip tone="neutral">Not set up on this server</Chip>
          </p>
          <p className="tiny muted" style={{ marginTop: 8, lineHeight: 1.6 }}>
            Create an app at developer.xero.com (Web app), register this redirect URI against it, and put its
            client id and secret in the server's <code>KEYSTONE_XERO_CLIENT_ID</code> and{" "}
            <code>KEYSTONE_XERO_CLIENT_SECRET</code>. Invoices sent in the meantime queue and go once it's
            connected.
          </p>
          <p className="mono tiny" style={{ wordBreak: "break-all", marginTop: 6 }}>
            {s.redirect_uri}
          </p>
        </>
      ) : !s.connected ? (
        <>
          <p>
            <Chip tone={s.status === "needs_reconnect" ? "crit" : "neutral"}>
              {s.status === "needs_reconnect" ? "Needs reconnecting" : "Not connected"}
            </Chip>
          </p>
          {s.last_error && <p className="tiny text-crit" style={{ marginTop: 6 }}>{s.last_error}</p>}
          {s.counts.pending > 0 && (
            <p className="tiny muted" style={{ marginTop: 6 }}>
              {s.counts.pending} invoice{s.counts.pending === 1 ? " is" : "s are"} waiting to go.
            </p>
          )}
          {canEdit && (
            <div className="btn-row" style={{ marginTop: 10 }}>
              <button className="btn btn-primary" type="button" disabled={connect.isPending} onClick={() => connect.mutate()}>
                {connect.isPending ? "Opening Xero…" : "Connect Xero"}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <dl className="kv">
            <dt>Organisation</dt>
            <dd>
              <Chip tone="ok" dot>
                {s.tenant_name ?? "Connected"}
              </Chip>
            </dd>
            <dt>In Xero</dt>
            <dd className="mono">{s.counts.pushed}</dd>
            <dt>Waiting</dt>
            <dd className="mono">{s.counts.pending + s.counts.retrying}</dd>
            <dt>Needs a look</dt>
            <dd className={`mono${s.counts.failed + s.counts.mismatch ? " text-crit" : ""}`}>
              {s.counts.failed + s.counts.mismatch}
            </dd>
          </dl>

          {attention.data && attention.data.filter((r) => r.status === "failed" || r.status === "mismatch").length > 0 && (
            <ul className="plainlist" style={{ marginTop: 10 }}>
              {attention.data
                .filter((r) => r.status === "failed" || r.status === "mismatch")
                .map((row) => (
                  <li key={row.id}>
                    <Link to={`/invoices/${row.invoice_id}`} className="mono">
                      {row.invoice_number}
                    </Link>{" "}
                    <span className="muted">{money(row.total_cents)}</span>
                    <div className="tiny text-crit">{row.last_error}</div>
                    {canEdit && row.status === "failed" && (
                      <button className="linkish tiny" type="button" onClick={() => retry.mutate(row.id)}>
                        Fixed it in Xero — try again
                      </button>
                    )}
                  </li>
                ))}
            </ul>
          )}

          {canEdit && (
            <div className="btn-row" style={{ marginTop: 12 }}>
              <button className="btn" type="button" disabled={push.isPending} onClick={() => push.mutate()}>
                {push.isPending ? "Pushing…" : "Push now"}
              </button>
              <button
                className="btn"
                type="button"
                disabled={disconnect.isPending}
                onClick={() => {
                  if (window.confirm("Disconnect Xero? Invoices will queue until it's connected again.")) {
                    disconnect.mutate();
                  }
                }}
              >
                Disconnect
              </button>
            </div>
          )}
          {push.data && (
            <p className="tiny muted" style={{ marginTop: 8 }}>
              {push.data.pushed} pushed · {push.data.waiting} waiting · {push.data.failed + push.data.mismatched} need a look
            </p>
          )}
        </>
      )}
    </Card>
  );
}
